import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Checkbox,
  Radio,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import Button from "../Button/Button";
import useBreakpoint from "../../../../hooks/useBreakpoint";
import { GuardiaRow } from "../Table/GuardiaTable";
import { requiereTractor, unidadesDePersona } from "../Table/utils/exitUnitRules";
import { tipoLabel } from "../Table/utils/vehicleInsideRow";
import TractorRetiraField from "./TractorRetiraField";
import { useVehiclesInside } from "../../../../views/LogisticPark/RegistroIngreso/hooks/useVehiclesInside";
import type { ExitUnitSelection } from "../../../../views/LogisticPark/RegistroIngreso/utils/exitPrefillUtils";
import "./ExitDialog.css";

/** Suficiente para no depender del default del endpoint: nadie entra con más unidades que esto. */
const LIMIT_UNIDADES = 50;

type PersonExitDialogProps = {
  /** Persona sobre la que se apretó "Salida". */
  guardia: GuardiaRow;
  onClose: () => void;
  /** Devuelve las unidades que salen con la persona. Vacío ⇒ sale a pie. */
  onConfirm: (selected: ExitUnitSelection[]) => void;
};

/** Unidad ofrecida en el modal, siempre resuelta contra `getVehiclesInside`. */
type UnidadElegible = ExitUnitSelection & {
  /** Renglón secundario de la opción, p. ej. el dock. */
  detalle: string;
};

const unitLabel = (unit: UnidadElegible) => {
  // `tipoLabel` es estructural: le alcanza con el rol cuando no hay fila con carrocería.
  const tipo = tipoLabel(unit.row ?? { role: unit.role });
  return [tipo === "—" ? "" : tipo, unit.plate].filter(Boolean).join(" ") || "Unidad";
};

/**
 * Paso de selección previo al formulario de egreso: la persona sale a pie o con sus unidades.
 *
 * No escribe nada. Al confirmar se navega al formulario de egreso con la persona y las unidades ya
 * precargadas, y ese formulario es el que hace el `addEvent` —igual que el modal por unidad—.
 *
 * Se monta sólo cuando está abierto: `useVehiclesInside` no limpia lo que trajo, así que dejarlo
 * montado mostraría las unidades de la persona anterior.
 */
const PersonExitDialog: React.FC<PersonExitDialogProps> = ({ guardia, onClose, onConfirm }) => {
  const { isMobile } = useBreakpoint();

  // El acompañante no maneja: hereda el vehículo del chofer, así que ofrecerle unidades haría que
  // el camión se registre dos veces.
  const esAcompanante = Boolean(guardia.mainEvent);

  const { vehiclesInside, isLoading } = useVehiclesInside({
    search: guardia.dni,
    limit: LIMIT_UNIDADES,
    enabled: !esAcompanante,
  });

  /**
   * Unidades ofrecidas, siempre según `getVehiclesInside` — es la única fuente que sabe si la
   * unidad sigue adentro. El evento de ingreso es una foto vieja: una unidad que ya salió (con otro
   * chofer, o por otra puerta) sigue apareciendo ahí, así que no sirve como plan B — ofrecerla haría
   * creer que todavía está para retirar.
   */
  const unidades = useMemo<UnidadElegible[]>(() => {
    if (esAcompanante) return [];

    return unidadesDePersona(vehiclesInside, guardia).map((row) => ({
      plate: row.plate,
      role: row.role,
      row,
      detalle: row.dock ? `Dock ${row.dock} · dentro del complejo` : "dentro del complejo",
    }));
  }, [esAcompanante, vehiclesInside, guardia]);

  const [retiraUnidades, setRetiraUnidades] = useState(true);
  const [destildadas, setDestildadas] = useState<string[]>([]);
  const [patenteTractor, setPatenteTractor] = useState("");
  const [errorTractor, setErrorTractor] = useState("");

  const llevaUnidades = retiraUnidades && unidades.length > 0;

  const seleccionadas: ExitUnitSelection[] = llevaUnidades
    ? unidades
        .filter((unit) => !destildadas.includes(unit.plate))
        .map(({ plate, role, row }) => ({ plate, role, row }))
    : [];

  const seleccionadasPlates = seleccionadas.map((unit) => (unit.plate ?? "").toUpperCase());

  /** Un acoplado no puede salir solo, tampoco cuando lo saca su propio chofer. */
  const faltaTractorEnSeleccion = requiereTractor(seleccionadas);

  const quedanDentro = unidades.filter(
    (unit) => !seleccionadasPlates.includes(unit.plate.toUpperCase())
  );

  const togglePlate = (plate: string) =>
    setDestildadas((prev) =>
      prev.includes(plate) ? prev.filter((other) => other !== plate) : [...prev, plate]
    );

  const handleConfirm = () => {
    const faltaTractor = faltaTractorEnSeleccion && !patenteTractor.trim();
    setErrorTractor(faltaTractor ? "Ingresá la patente del tractor que retira el acoplado." : "");
    if (faltaTractor) return;

    const selected = [...seleccionadas];
    // El tractor cargado entra siempre: si está dentro también sale, y si es externo igual tiene
    // que ser el vehículo principal del egreso —un acoplado no puede serlo—.
    if (patenteTractor.trim()) {
      const tractorDentro = unidades.find(
        (unit) => (unit.plate ?? "").toUpperCase() === patenteTractor.trim().toUpperCase()
      );
      selected.push({
        plate: tractorDentro?.plate ?? patenteTractor.trim().toUpperCase(),
        role: "tractor",
        row: tractorDentro?.row,
      });
    }

    onConfirm(selected);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: "ve-paper" }}
    >
      <DialogTitle disableTypography className="ve-head">
        <div>
          <h2>Registrar salida — {guardia.nombre}</h2>
          <p>
            DNI {guardia.dni}
            {guardia.transportCompanyName ? ` · ${guardia.transportCompanyName}` : ""}
          </p>
        </div>
        <IconButton onClick={onClose} aria-label="Cerrar" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="ve-body">
        {isLoading ? (
          <div className="ve-loading">
            <CircularProgress size={32} />
          </div>
        ) : unidades.length === 0 ? (
          <p className="ve-hint">
            Esta persona no tiene unidades a cargo dentro del complejo: se registra su salida a pie.
          </p>
        ) : (
          <>
            <h4 className="ve-block-title">
              ¿Cómo sale? <span className="ve-req">*</span>
            </h4>

            <label className={retiraUnidades ? "ve-opt ve-opt-sel" : "ve-opt"}>
              <Radio
                checked={retiraUnidades}
                onChange={() => setRetiraUnidades(true)}
                color="default"
                size="small"
              />
              <span>
                <b>
                  Sale con su{unidades.length > 1 ? "s" : ""} unidad
                  {unidades.length > 1 ? "es" : ""}
                </b>
                <small>Se registra la salida de la persona y de las unidades tildadas.</small>
              </span>
            </label>
            <label className={!retiraUnidades ? "ve-opt ve-opt-sel" : "ve-opt"}>
              <Radio
                checked={!retiraUnidades}
                onChange={() => setRetiraUnidades(false)}
                color="default"
                size="small"
              />
              <span>
                <b>Sale a pie</b>
                <small>
                  La persona sale del complejo y las unidades quedan dentro, sin chofer asignado.
                </small>
              </span>
            </label>

            {retiraUnidades && (
              <div className="ve-sub">
                <h4 className="ve-block-title">Unidades que retira</h4>
                <p className="ve-hint">
                  Destildá las que quedan dentro del complejo (desacople parcial).
                </p>
                {unidades.map((unit) => {
                  const checked = !destildadas.includes(unit.plate);
                  return (
                    <label
                      key={unit.row?.key ?? unit.plate}
                      className={checked ? "ve-opt ve-opt-sel" : "ve-opt"}
                    >
                      <Checkbox
                        checked={checked}
                        onChange={() => togglePlate(unit.plate)}
                        color="default"
                        size="small"
                      />
                      <span>
                        <b>{unitLabel(unit)}</b>
                        <small>{unit.detalle}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {faltaTractorEnSeleccion && (
              <TractorRetiraField
                value={patenteTractor}
                onChange={(value) => {
                  setPatenteTractor(value);
                  if (errorTractor) setErrorTractor("");
                }}
                error={errorTractor}
                // Sólo se trajeron las unidades de esta persona: el tractor que viene a buscar el
                // acoplado casi siempre es de otro ingreso, así que se carga a mano.
                candidates={[]}
                selectedPlates={seleccionadasPlates}
              />
            )}

            {quedanDentro.length > 0 && (
              <div className="ve-alert">
                <WarningRoundedIcon fontSize="small" />
                <div>
                  <b>
                    {quedanDentro.map((unit) => unit.plate).join(", ")}{" "}
                    {quedanDentro.length > 1 ? "quedan" : "queda"} dentro del complejo.
                  </b>
                  <br />
                  {quedanDentro.length > 1 ? "Van a figurar" : "Va a figurar"} como “Sin chofer” en
                  el listado de unidades.
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className="ve-foot">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="neutral" onClick={handleConfirm} disabled={isLoading}>
          Dar salida
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonExitDialog;
